/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  computed,
  signal,
} from '@angular/core';
import { interval, Subscription } from 'rxjs';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);

@Component({
  selector: 'logibids-countdown',
  templateUrl: './countdown.component.html',
  styleUrls: ['./countdown.component.css'],
  standalone: true,
})
export class CountdownComponent implements OnInit, OnChanges, OnDestroy {
  @Input() end!: string;
  @Output() timerDone = new EventEmitter<{
    hours: number;
    mins: number;
    secs: number;
  }>();

  private now = signal(dayjs());
  private countdownSub?: Subscription;
  private hasEmittedDone = false;

  /** Single computed source of truth */
  readonly timeLeft = computed(() => {
    if (!this.end) {
      return { hours: 0, mins: 0, secs: 0 };
    }

    const diff = dayjs(this.end).diff(this.now(), 'second');

    if (diff <= 0) {
      return { hours: 0, mins: 0, secs: 0 };
    }

    const d = dayjs.duration(diff, 'seconds');

    return {
      hours: Math.floor(d.asHours()),
      mins: d.minutes(),
      secs: d.seconds(),
    };
  });

  readonly isActive = computed(() => {
    const t = this.timeLeft();
    return t.hours > 0 || t.mins > 0 || t.secs > 0;
  });

  ngOnInit() {
    this.startTimer();
  }

  ngOnChanges() {
    this.hasEmittedDone = false;
    this.startTimer();
  }

  private startTimer() {
    this.countdownSub?.unsubscribe();

    this.countdownSub = interval(1000).subscribe(() => {
      this.now.set(dayjs());

      const t = this.timeLeft();

      if (
        !this.hasEmittedDone &&
        t.hours === 0 &&
        t.mins === 0 &&
        t.secs === 0
      ) {
        this.hasEmittedDone = true;
        this.timerDone.emit(t); // ✅ emit once
        this.countdownSub?.unsubscribe();
      }
    });
  }

  ngOnDestroy() {
    this.countdownSub?.unsubscribe();
  }
}
